import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import { dashboardApi } from "../../api/dashboardApi";
import { useAuth } from "../../auth/AuthContext";
import "../../styles/irinox.css";

type EnergySeries = { label: string; data: number[] };
type ApplianceRow = {
  serialNumber: string;
  name?: string;
  consumption?: number;
  expense?: number;
  co2?: number;
  labName?: string;
  role?: string;
};

export function IrinoxEnergyOverview() {
  const { token } = useAuth() as { token: string | null };
  const [aggregate, setAggregate] = useState<{
    series: EnergySeries[];
    labels: string[];
  } | null>(null);
  const [appliances, setAppliances] = useState<ApplianceRow[]>([]);
  const [userInfo, setUserInfo] = useState<{
    country?: string;
    firstName?: string | null;
    lastName?: string | null;
    companyName?: string | null;
    email?: string | null;
    language?: string | null;
    brand?: string | null;
    role?: string | null;
    status?: string | null;
    preferences?: Record<string, any>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    setLoading(true);
    setErr(null);

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const startTimestamp = todayStart.getTime();

    Promise.all([
      dashboardApi.getIrinoxEnergyAggregate(token, startTimestamp),
      dashboardApi.getIrinoxEnergyAppliances(token, startTimestamp),
      dashboardApi.getIrinoxUserInfo(token),
    ])
      .then(([aggRes, appRes, userRes]) => {
        setAggregate(aggRes);
        setAppliances(Array.isArray(appRes) ? appRes : []);
        setUserInfo(userRes ?? null);
      })
      .catch((e) => {
        setErr(e?.message ?? "Errore caricamento energia");
        setAggregate(null);
        setAppliances([]);
        setUserInfo(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const consumptionSeries = aggregate?.series?.find(
    (s) =>
      s.label?.toLowerCase().includes("consumption") ||
      s.label?.toLowerCase().includes("consumo") ||
      s.label?.toLowerCase().includes("estimatedconsumption")
  );
  const expenseSeries = aggregate?.series?.find(
    (s) =>
      s.label?.toLowerCase().includes("expense") ||
      s.label?.toLowerCase().includes("spesa") ||
      s.label?.toLowerCase().includes("estimatedexpense")
  );

  const chartData =
    aggregate?.labels?.map((lbl, i) => ({
      x: lbl,
      consumption: Number(consumptionSeries?.data?.[i] ?? 0),
      expense: Number(expenseSeries?.data?.[i] ?? 0),
    })) ?? [];

  /** Fallback: grafico consumo per dispositivo quando aggregate è vuoto ma appliances ha dati */
  const fallbackChartData =
    chartData.length === 0 && appliances.length > 0
      ? appliances
          .filter((a) => (a.consumption ?? 0) > 0)
          .map((a) => ({
            x: a.name ?? a.serialNumber ?? "—",
            consumption: a.consumption ?? 0,
            expense: a.expense ?? 0,
          }))
      : [];
  const chartDataToShow = chartData.length > 0 ? chartData : fallbackChartData;

  const totConsumption = appliances.reduce((a, r) => a + (r.consumption ?? 0), 0);
  const totExpense = appliances.reduce((a, r) => a + (r.expense ?? 0), 0);
  const totCo2 = appliances.reduce((a, r) => a + (r.co2 ?? 0), 0);

  if (loading) {
    return (
      <div className="card machines-section-card shadow-sm">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-center py-5 text-muted">
            <span className="spinner-border spinner-border-sm me-2" />
            Caricamento energia aggregata…
          </div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="card machines-section-card shadow-sm">
        <div className="card-body">
          <div className="alert alert-warning mb-0">
            <i className="bi bi-exclamation-triangle me-2" />
            {err}
          </div>
        </div>
      </div>
    );
  }

  const hasData = chartDataToShow.length > 0 || appliances.length > 0;

  if (!hasData) {
    return (
      <div className="card machines-section-card shadow-sm">
        <div className="card-header fw-semibold">Energia aggregata (FreshCloud)</div>
        <div className="card-body">
          <div className="text-muted small">Nessun dato disponibile per oggi.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="irinox-card mb-4">
      <div className="irinox-card-header">
        <i className="bi bi-lightning-charge" />
        Energia aggregata (FreshCloud)
      </div>
      <div className="card-body">
        {/* Profilo FreshCloud */}
        {userInfo && (
          <div className="mb-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="small text-muted">
              Profilo FreshCloud:&nbsp;
              <strong>
                {userInfo.firstName} {userInfo.lastName}
              </strong>
              {userInfo.companyName ? ` • ${userInfo.companyName}` : ""}
              {userInfo.email ? ` • ${userInfo.email}` : ""}
            </div>
            <div className="d-flex flex-wrap gap-2 small">
              {userInfo.language && (
                <span className="badge bg-light text-dark">
                  <i className="bi bi-translate me-1" />
                  {userInfo.language}
                </span>
              )}
              {userInfo.preferences?.measurement_system && (
                <span className="badge bg-light text-dark">
                  <i className="bi bi-rulers me-1" />
                  {userInfo.preferences.measurement_system === "metric"
                    ? "Sistema metrico"
                    : userInfo.preferences.measurement_system}
                </span>
              )}
              {userInfo.preferences?.hours_display_system && (
                <span className="badge bg-light text-dark">
                  <i className="bi bi-clock-history me-1" />
                  {userInfo.preferences.hours_display_system === "24h"
                    ? "Formato 24h"
                    : userInfo.preferences.hours_display_system}
                </span>
              )}
            </div>
          </div>
        )}
        {/* KPI */}
        <div className="row g-4 mb-4">
          <div className="col-6 col-md-4">
            <div className="irinox-kpi-card">
              <div className="irinox-kpi-icon">
                <i className="bi bi-lightning-charge" />
              </div>
              <div className="irinox-kpi-label">Consumo totale (oggi)</div>
              <div className="irinox-kpi-value">{totConsumption.toFixed(1)} kWh</div>
            </div>
          </div>
          <div className="col-6 col-md-4">
            <div className="irinox-kpi-card">
              <div className="irinox-kpi-icon">
                <i className="bi bi-currency-euro" />
              </div>
              <div className="irinox-kpi-label">Spesa totale</div>
              <div className="irinox-kpi-value">€ {totExpense.toFixed(2)}</div>
            </div>
          </div>
          <div className="col-6 col-md-4">
            <div className="irinox-kpi-card">
              <div className="irinox-kpi-icon">
                <i className="bi bi-cloud" />
              </div>
              <div className="irinox-kpi-label">CO₂ equivalente</div>
              <div className="irinox-kpi-value">{totCo2.toFixed(1)} kg</div>
            </div>
          </div>
        </div>

        {/* Grafico aggregato o fallback consumo per dispositivo */}
        {chartDataToShow.length > 0 && (
          <div className="mb-4 irinox-chart-container" style={{ minHeight: 260 }}>
            <ResponsiveContainer width="100%" height={260} minHeight={200}>
              <ComposedChart
                data={chartDataToShow}
                margin={{ top: 10, right: 50, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0f2fe" />
                <XAxis dataKey="x" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} label={{ value: "kWh", angle: -90 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit=" €" />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    name === "consumption"
                      ? `${Number(value).toFixed(2)} kWh`
                      : `€ ${Number(value).toFixed(2)}`,
                    name === "consumption" ? "Consumo" : "Spesa",
                  ]}
                />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="consumption"
                  fill="#0ea5e9"
                  name="Consumo (kWh)"
                  radius={[8, 8, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="expense"
                  stroke="#0284c7"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name="Spesa (€)"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Tabella per appliance */}
        {appliances.length > 0 && (
          <div>
            <h6 className="text-muted mb-3 fw-semibold">Dettaglio per dispositivo</h6>
            <div className="table-responsive">
              <table className="table irinox-table mb-0">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Serial</th>
                    <th>Lab / Categoria</th>
                    <th className="text-end">Consumo (kWh)</th>
                    <th className="text-end">Spesa (€)</th>
                    <th className="text-end">CO₂ (kg)</th>
                  </tr>
                </thead>
                <tbody>
                  {appliances.map((a) => (
                    <tr key={a.serialNumber}>
                      <td>{a.name || "—"}</td>
                      <td>
                        <code className="small">{a.serialNumber}</code>
                      </td>
                      <td>{a.labName || a.role || "—"}</td>
                      <td className="text-end">
                        {a.consumption != null ? a.consumption.toFixed(1) : "—"}
                      </td>
                      <td className="text-end">
                        {a.expense != null ? `€ ${a.expense.toFixed(2)}` : "—"}
                      </td>
                      <td className="text-end">
                        {a.co2 != null ? a.co2.toFixed(1) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
